// IT 監控 › 網路與應用（`kmgr-network` `/kmgr/network`，SPEC #kinio-kms-ui 導覽表）：網路設備｜應用探針｜收集器。
// 資料：範圍內已啟用家戶的 targets（kind = probe：ping / snmp = 網路設備、http / tcp / file = 應用探針；kind = agent 帶 collector = 收集器）。
// 收集器離線時整頁頂「讀不到網路設備（收集器〈名稱〉離線 N 分鐘）」、探針的結果一律畫 ⊘ 讀不到、不出現綠色的網路數字（KMS-25，UX-04 其他區塊照常）。
// SNMP 明細（介面、PoE、溫度）kmgr 0.11.0 還不帶：有就畫、沒有寫「—」。拓樸圖（M2-03 相依抑制）放第二層，位置留在網路設備表之後（data-testid km-topology-slot）。
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { stamp } from '../../lib/timefmt';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { useKmgrDashboard, useKmgrEnv } from './hooks';
import { isNetworkDevice, probeState } from './model';
import { Crumbs, LiveStamp, NS, osText, StateBox } from './parts';
import { HostStatePill, NetUnreadableBar, ProbeStatePill, SettingsEntry } from './widgets';
import { hostHref } from './HostsPage';
import { UpsTab, useHomesPower } from './power/Ups';
import type { HomeStatus, HostRow, Probe } from './types';

type Tab = 'devices' | 'probes' | 'collectors' | 'ups';
const TABS: Tab[] = ['devices', 'probes', 'collectors', 'ups'];

type TFn = (k: string, o?: Record<string, unknown>) => string;

/** 探針的目標一句：ping / snmp = 位址；tcp = 位址:埠；http = scheme://位址/路徑；file = 路徑。 */
export function probeTarget(p: Probe): string {
  switch (p.kind) {
    case 'tcp':
      return `${p.target ?? ''}:${p.port ?? ''}`;
    case 'http':
      return `${p.http?.scheme ?? 'https'}://${p.target ?? ''}${p.http?.path ?? ''}`;
    case 'file':
      return p.file?.path ?? '';
    default:
      return p.target ?? '';
  }
}

/** 讀取方式：SNMP 唯讀 / 只用 ping。 */
export function probeMethod(t: TFn, p: Probe): string {
  return p.kind === 'snmp' ? t('net.method_snmp') : t('net.method_ping');
}

/** 狀態文字（照狀態與種類）：可以讀取 · 24 個介面 / 有回應 / 沒有回應 / 讀取失敗（碼 N）/ 沒有收集器負責 / 還沒有結果 / 讀不到。 */
export function probeStateText(t: TFn, p: Probe, unreadable: boolean): { state: ReturnType<typeof probeState>; text: string } {
  const st = probeState(p, unreadable);
  const ifs = p.snmp?.interfaces;
  switch (st) {
    case 'ok':
      return { state: st, text: p.kind === 'snmp' ? (ifs ? t('net.ok_snmp_n', { n: ifs }) : t('net.ok_snmp')) : p.kind === 'ping' ? t('net.ok_ping') : t('net.ok_probe') };
    case 'bad':
      return { state: st, text: p.kind === 'snmp' ? t('net.bad_snmp', { code: p.last_error ?? '' }) : p.kind === 'ping' ? t('net.bad_ping') : t('net.bad_probe', { code: p.last_error ?? '' }) };
    case 'unassigned':
      return { state: st, text: t('net.unassigned') };
    case 'noresult':
      return { state: st, text: t('net.noresult') };
    default:
      return { state: st, text: t('home.unreadable') };
  }
}

export function NetworkPage() {
  const { t } = useTranslation(NS);
  useKmgrEnv();
  const { homes, planes, orgs } = useScope();
  const [sp] = useSearchParams();
  // ?class=ups（用電頁、全域搜尋的連結）= UPS 分頁
  const tab = (sp.get('class') === 'ups' ? 'ups' : TABS.includes(sp.get('tab') as Tab) ? sp.get('tab') : 'devices') as Tab;
  const onlyHome = sp.get('home');
  const dash = useKmgrDashboard();
  const items = (dash.data?.homes.items ?? []).filter((h) => !onlyHome || h.home_id === onlyHome);
  const apiOf = (id: string) => homes.find((x) => x.home_id === id)?.endpoints?.api ?? planes[0]?.api ?? '';
  const probeQs = useQueries({ queries: items.map((h) => ({ queryKey: ['kmgr', 'probes', h.home_id], queryFn: () => kmgrApi.probes(apiOf(h.home_id), h.home_id), refetchInterval: 15_000 })) });
  const hostQs = useQueries({ queries: items.map((h) => ({ queryKey: ['kmgr', 'home-hosts', h.home_id], queryFn: () => kmgrApi.homeHosts(apiOf(h.home_id), h.home_id), refetchInterval: 15_000 })) });
  const crossOrg = new Set(items.map((h) => h.org_id)).size > 1 || orgs.filter((o) => o.kind === 'team').length > 1;
  const loading = dash.loading || probeQs.some((q) => q.isLoading) || hostQs.some((q) => q.isLoading);
  const unreadableHomes = items.filter((h) => h.network?.source_state === 'unreadable');
  const probes = items.flatMap((h, i) => (probeQs[i]?.data ?? []).map((p) => ({ p, h, unreadable: h.network?.source_state === 'unreadable' })));
  const collectors = items.flatMap((h, i) => (hostQs[i]?.data ?? []).filter((x) => x.collector).map((x) => ({ x, h })));
  const updatedAt = Math.max(dash.updatedAt, ...probeQs.map((q) => q.dataUpdatedAt || 0));
  const failed = probeQs.filter((q) => q.isError && !q.data).length + hostQs.filter((q) => q.isError && !q.data).length;
  const devices = probes.filter((r) => isNetworkDevice(r.p));
  const apps = probes.filter((r) => !isNetworkDevice(r.p));
  const powerQs = useHomesPower(items);
  const upsCount = powerQs.reduce((a, q) => a + (q.data?.ups.length ?? 0), 0);
  const homeLabel = (h: HomeStatus) => (crossOrg && h.org_name ? `${h.org_name} › ${h.home_name}` : h.home_name);
  const settingsOf = (h: HomeStatus, sub: string) => `/sites/kmgr/${encodeURIComponent(h.home_id)}/${sub}`;
  return (
    <section className="page km" data-testid="km-network-page" data-unreadable={unreadableHomes.length ? '1' : undefined}>
      <Crumbs items={[{ label: t('nav.group'), to: '/kmgr/incidents' }, { label: t('nav.network') }]} />
      <div className="km-head">
        <h1 className="page-title">{t('nav.network')}</h1>
        <span className="spacer" />
        <LiveStamp updatedAt={updatedAt} stale={dash.lastErrorAt > dash.updatedAt} />
        <SettingsEntry />
      </div>
      {unreadableHomes.map((h) => (
        <NetUnreadableBar key={h.home_id} offline={h.network?.collectors_offline ?? []} reason={h.network?.reason} homeId={h.home_id} />
      ))}
      <nav className="km-tabs" aria-label={t('net.tabs')}>
        <Link to="/kmgr/network" aria-current={tab === 'devices' ? 'page' : undefined}>
          {loading ? t('net.tab_devices') : t('net.tab_devices_n', { n: devices.length })}
        </Link>
        <Link to="/kmgr/network?tab=probes" aria-current={tab === 'probes' ? 'page' : undefined}>
          {loading ? t('net.tab_probes') : t('net.tab_probes_n', { n: apps.length })}
        </Link>
        <Link to="/kmgr/network?tab=collectors" aria-current={tab === 'collectors' ? 'page' : undefined}>
          {loading ? t('net.tab_collectors') : t('net.tab_collectors_n', { n: collectors.length })}
        </Link>
        {(upsCount > 0 || tab === 'ups') && (
          <Link to="/kmgr/network?tab=ups" aria-current={tab === 'ups' ? 'page' : undefined} data-testid="km-tab-ups">
            {t('net.tab_ups_n', { n: upsCount })}
          </Link>
        )}
      </nav>
      {loading ? (
        <div className="card">
          <SkeletonLines n={4} label={t('common.loading')} />
        </div>
      ) : !items.length ? (
        <StateBox icon="○" title={t('net.no_homes')} body={t('net.no_homes_body')} testId="km-network-empty" />
      ) : (
        <>
          {failed > 0 && (
            <div className="km-bar unk" role="status">
              <span className="ic" aria-hidden>
                ⊘
              </span>
              <span className="txt">{t('net.partial_failed', { n: failed })}</span>
              <button className="btn btn-sm" onClick={() => [...probeQs, ...hostQs].forEach((q) => void q.refetch())}>
                {t('common.retry')}
              </button>
            </div>
          )}
          {tab === 'devices' && (
            <>
              {devices.length ? (
                <div className="card">
                  <table className="km-table" data-testid="km-net-devices">
                    <thead>
                      <tr>
                        <th>{t('net.col_name')}</th>
                        {items.length > 1 && <th>{crossOrg ? t('hosts.col_org_home') : t('hosts.col_home')}</th>}
                        <th>{t('net.col_addr')}</th>
                        <th>{t('net.col_method')}</th>
                        <th>{t('hosts.col_state')}</th>
                        <th>{t('net.col_ifs')}</th>
                        <th>{t('net.col_poe')}</th>
                        <th>{t('net.col_temp')}</th>
                        <th>{t('net.col_collector')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map(({ p, h, unreadable }) => {
                        const st = probeStateText(t, p, unreadable);
                        const sn = p.snmp;
                        return (
                          <tr key={p.probe_id} data-probe={p.probe_id}>
                            <td>
                              <strong>{p.name || p.target}</strong>
                            </td>
                            {items.length > 1 && <td>{homeLabel(h)}</td>}
                            <td className="tabular">{p.target}</td>
                            <td>{probeMethod(t, p)}</td>
                            <td>
                              <ProbeStatePill state={st.state} text={st.text} />
                            </td>
                            <td className="tabular">{unreadable || !sn?.interfaces ? '—' : sn.interfaces_down ? t('net.ifs_down', { n: sn.interfaces, down: sn.interfaces_down }) : String(sn.interfaces)}</td>
                            <td className="tabular">{unreadable || sn?.poe_w === undefined ? '—' : sn.poe_budget_w ? `${sn.poe_w} / ${sn.poe_budget_w} W` : `${sn.poe_w} W`}</td>
                            <td className="tabular">{unreadable || sn?.temperature_c === undefined ? '—' : `${sn.temperature_c} °C`}</td>
                            <td>{p.collector_name || (p.collector ? t('net.collector_unnamed') : '—')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* 拓樸圖（M2-03 相依抑制：Kinio Station MAC → 埠、NVR → 通道、SNMP 介面表）放這裡的第二層；kmgr 有拓樸端點再畫 */}
                  <div data-testid="km-topology-slot" hidden />
                  <p className="muted km-sub" style={{ marginBottom: 0 }}>
                    {t('net.devices_note')}
                  </p>
                </div>
              ) : (
                <StateBox icon="○" title={t('net.devices_none')} body={t('net.devices_none_body')} testId="km-net-devices-empty">
                  <Link className="btn" to={settingsOf(items[0], 'collectors')}>
                    {t('net.setup_collector')}
                  </Link>
                </StateBox>
              )}
            </>
          )}
          {tab === 'probes' && (
            <>
              {apps.length ? (
                <div className="card">
                  <table className="km-table" data-testid="km-net-probes">
                    <thead>
                      <tr>
                        <th>{t('net.col_name')}</th>
                        {items.length > 1 && <th>{crossOrg ? t('hosts.col_org_home') : t('hosts.col_home')}</th>}
                        <th>{t('net.col_target')}</th>
                        <th>{t('net.col_kind')}</th>
                        <th>{t('hosts.col_state')}</th>
                        <th>{t('net.col_last')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apps.map(({ p, h, unreadable }) => {
                        const st = probeStateText(t, p, unreadable);
                        return (
                          <tr key={p.probe_id} data-probe={p.probe_id}>
                            <td>
                              <strong>{p.name || probeTarget(p)}</strong>
                            </td>
                            {items.length > 1 && <td>{homeLabel(h)}</td>}
                            <td className="tabular">{probeTarget(p)}</td>
                            <td>{t(`probe.kind_${p.kind}`)}</td>
                            <td>
                              <ProbeStatePill state={st.state} text={st.text} />
                            </td>
                            <td>{p.last_at ? stamp(p.last_at) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <StateBox icon="○" title={t('net.probes_none')} body={t('net.probes_none_body')} testId="km-net-probes-empty">
                  <Link className="btn" to={settingsOf(items[0], 'probes')}>
                    {t('net.setup_probe')}
                  </Link>
                </StateBox>
              )}
            </>
          )}
          {tab === 'ups' && <UpsTab items={items} homeLabel={homeLabel} crossOrg={crossOrg} />}
          {tab === 'collectors' && (
            <>
              {collectors.length ? (
                <div className="card">
                  <table className="km-table" data-testid="km-net-collectors">
                    <thead>
                      <tr>
                        <th>{t('net.col_name')}</th>
                        {items.length > 1 && <th>{crossOrg ? t('hosts.col_org_home') : t('hosts.col_home')}</th>}
                        <th>{t('token.col_os')}</th>
                        <th>{t('hosts.col_state')}</th>
                        <th>{t('net.col_scan')}</th>
                        <th>{t('net.col_applied')}</th>
                        <th>{t('hosts.col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collectors.map(({ x, h }) => (
                        <tr key={x.target_ref} data-host={x.target_ref}>
                          <td>
                            <Link to={hostHref(x)}>
                              <strong>{x.hostname || t('target.host')}</strong>
                            </Link>
                          </td>
                          {items.length > 1 && <td>{homeLabel(h)}</td>}
                          <td>{osText(x.os) || '—'}</td>
                          <td>
                            <HostStatePill h={x} />
                          </td>
                          <td className="tabular">{x.collector!.assignments.length ? x.collector!.assignments.join('、') : <span className="muted">{t('host.collector_none')}</span>}</td>
                          <td>
                            <AppliedText c={x.collector!} />
                          </td>
                          <td>
                            <Link className="btn btn-sm" to={settingsOf(h, 'collectors')}>
                              {t('org.manage')}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <StateBox icon="○" title={t('net.collectors_none')} body={t('net.collectors_none_body')} testId="km-net-collectors-empty">
                  <Link className="btn" to={settingsOf(items[0], 'collectors')}>
                    {t('net.setup_collector')}
                  </Link>
                </StateBox>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

/** 收集器的設定套用狀態：已套用 v3 / 待套用 v3（目前 v2）/ 本機沒開（錯誤）。 */
export function AppliedText({ c }: { c: NonNullable<HostRow['collector']> }) {
  const { t } = useTranslation(NS);
  if (c.last_error) return <span className="km-metric attention">{t('host.collector_error', { code: c.last_error })}</span>;
  if (c.assignments_version === undefined) return <>—</>;
  if (c.delivered_version === c.assignments_version) return <>{t('net.applied_v', { v: c.assignments_version })}</>;
  return <span className="km-metric attention">{t('net.pending_v', { v: c.assignments_version, cur: c.delivered_version ?? 0 })}</span>;
}

